/**
 * ItemDetailModal Component
 * Provides a detailed slide-out view for a single product, including 
 * price history visualization and a shared notepad for comments.
 */

import React, { useState, useEffect } from 'react';
import { LuX, LuMessageSquare, LuCheck, LuTrash2, LuChevronLeft } from "react-icons/lu";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getPriceHistory } from '../services/api'; 
import '../styles/item-modal.css';

const ItemDetailModal = ({ item,
  onClose,
  onDelete,
  onMarkPurchased,
  onAddNote,
  onDeleteComment,
  isCartPage,
  isArchived = false }) => {
  const [view, setView] = useState('details'); // Toggle between 'details' and 'comments' view
  const [newNote, setNewNote] = useState(''); // State for the comment input field
  const [purchaseState, setPurchaseState] = useState('idle'); // states: idle → confirm → processing
  const [priceHistory, setPriceHistory] = useState([]); // State for chart data

  // Side effect to fetch historical price data for the specific item whenever the item ID changes.
  useEffect(() => {
    if (!item?.item_id) return;
    const fetchHistory = async () => {
      try {
        const data = await getPriceHistory(item.item_id);
        setPriceHistory(data);
      } catch (err) {
        console.error("Error fetching history:", err);
      }
    };
    fetchHistory();
  }, [item?.item_id]);

  if (!item) return null;

  const displayPrice = Number(item.price || 0).toFixed(2);

  const isLocked = isArchived || item?.wishlist_archived;

  const purchaseDisabled = isLocked || item.is_purchased;

  const getComments = () => {
    if (Array.isArray(item.comments)) return item.comments;
    if (Array.isArray(item.notes)) return item.notes;

    if (typeof item.notes === 'string') {
      try {
        const parsed = JSON.parse(item.notes);
        if (Array.isArray(parsed)) return parsed;
      } catch (err) {
        return item.notes
          .split('\n')
          .filter(line => line.trim() !== "")
          .map((line, index) => {
            const match = line.match(/^\[(.*?)\]:\s*(.*)$/);
            return {
              id: `legacy-${index}`,
              user: "Unknown",
              created_at: match ? new Date(match[1]).toISOString() : null,
              text: match ? match[2] : line
            };
          });
      }
    }

    return [];
  };

  // Who purchases what item on shared wishlist
  const showPurchasedBy =
  item.is_purchased === 1 &&
  item.purchased_by_username &&
  item.purchased_by_username !== "0" &&
  item.purchased_by_username !== 0;

  const comments = getComments();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Handles the submission of a new note/comment
  const handleNoteSubmit = async () => {
    if (newNote.trim() && onAddNote) {
      try {
        await onAddNote(item.item_id, newNote);
        setNewNote('');
      } catch (err) {
        console.error("Failed to save note:", err);
      }
    }
  };

  return (
    <div className="item-modal-overlay" onClick={onClose}>
      {/* Prevent click-through from closing the drawer when interacting with content */}
      <div className="item-modal-drawer" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <LuX size={24} />
        </button>

        {view === 'details' ? (
          <>
            {/* Visual Header: Product Image and Links */}
            <a href={item.product_url} target="_blank" rel="noreferrer" className="img-container">
              <img src={item.image_url} alt={item.product_name} />
              <div className="img-overlay">View Product</div>
            </a>

            <div className="details-section">
              <p className="store-name">{item.store_name}</p>
              <h2 className="product-name">{item.product_name}</h2>
              <p className="price-tag">${displayPrice}</p>
              {showPurchasedBy && (
                <p className="purchased-info">
                  Purchased by {item.purchased_by_username}
                </p>
              )}

              {isLocked && (
                <div className="archived-badge">
                  Archived Collection
                </div>
              )}
            </div>

            {/* Price Analytics: Visualizes cost fluctuations over time */}
            <div className="price-history">
              <h3 className="item-section-title">Price History</h3>
              <div className="history-chart-container">
                {priceHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={priceHistory}>
                      <XAxis
                        dataKey="date"
                        hide={priceHistory.length < 2}
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                      />
                      <YAxis hide domain={['dataMin - 10', 'dataMax + 10']} />
                      <Tooltip
                        contentStyle={{ 
                          borderRadius: '8px', 
                          border: 'none', 
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                        }}
                        formatter={(value) => [`$${Number(value).toFixed(2)}`, "Price"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="price"
                        stroke="#DB8046"
                        strokeWidth={2}
                        dot={priceHistory.length < 10}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="empty-history">
                    <p>Price monitoring active. No changes recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
          {/* Notes & Collaboration View */}
          <div className="notes-view">
            <button className="back-btn" onClick={() => setView('details')}>
              <LuChevronLeft size={20} /> Back
            </button>
            <h3 className="section-title">Notes & Comments</h3>

            <div className="notes-list">
              {comments.length > 0 ? (
                comments.map((comment, index) => {
                  const createdAt = comment.created_at ? new Date(comment.created_at).toLocaleDateString('en-US') : null;
                  const canDelete = onDeleteComment && comment.user === currentUser.username;

                  return (
                    <div key={comment.id || index} className="note-bubble">
                      <div className="note-meta">
                        <div className="note-meta-left">
                          <span className="note-author">{comment.user || 'Unknown'}</span>
                          {createdAt && <span className="note-date">{createdAt}</span>}
                        </div>
                        {canDelete && (
                          <button
                            className="comment-delete-btn"
                            onClick={() => onDeleteComment(item.item_id, comment.id)}
                            title="Delete comment"
                          >
                            <LuTrash2 size={14} />
                          </button>
                        )}
                      </div>
                      <p>{comment.text}</p>
                    </div>
                  );
                })
              ) : (
                <p className="empty-notes">No notes yet.</p>
              )}
            </div>

            {!isLocked && (
            <div className="note-input-area">
              <textarea
                placeholder="Add a comment..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button className="save-note-btn" onClick={handleNoteSubmit}>
                Save Note
              </button>
            </div>
            )}
          </div>
          </>
        )}

        {/* Global Action Bar: Handles communication, purchasing, and deletion */}
        <div className="action-bar">
          <button
            className={`action-btn ${view === 'comments' ? 'active-comment' : ''}`}
            onClick={() => setView(view === 'details' ? 'comments' : 'details')}
          >
            <LuMessageSquare size={20} />
          </button>

        {!purchaseDisabled && (
<button
  className={`action-btn purchase-btn ${purchaseState === 'confirm' ? 'confirming' : ''} ${purchaseState === 'processing' ? 'processing' : ''}`}
  disabled={purchaseState === 'processing'}
  onClick={async () => {
    if (purchaseState !== 'confirm') {
      setPurchaseState('confirm');
      setTimeout(() => setPurchaseState('idle'), 2500);
      return;
    }

    try {
      setPurchaseState('processing');

      await onMarkPurchased(item);

      onClose();
    } finally {
      setPurchaseState('idle');
    }
  }}
>
  {purchaseState === 'processing' ? (
    <span>Processing...</span>
  ) : purchaseState === 'confirm' ? (
    <span>Confirm?</span>
  ) : (
    <LuCheck size={20} />
  )}
</button>
        )}

        {!isLocked && (
          <button
            className="action-btn delete-btn"
            onClick={() => {
              if (window.confirm("Are you sure you want to delete this item?")) {
                onDelete(item.item_id);
              }
            }}
          >
            <LuTrash2 size={20} />
          </button>
        )}
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;